import { forwardRef } from 'react'

import CautionView from '@/components/detail/components/parkinglot/components/caution/view'
import ParkinglotDetailMoaiView from '@/components/detail/components/parkinglot/components/moai/view'
import ParkinglotModifyDateView from '@/components/detail/components/parkinglot/components/parkinglotModifyDate/view'
import TicketsListView from '@/components/detail/components/parkinglot/components/tickets/list/view'
import { AnimationSheet } from '@/components/detail/components/shared/animationSheet/view'
import AppBanner from '@/components/detail/components/shared/banner/view'
import ParkinglotDetailHeaderView from '@/components/detail/components/shared/header/view'
import ParkinglotInfoView from '@/components/detail/components/shared/parkinglotInfo/view'
import StaticNaverMap from '@/components/map/components/staticNaverMap'
import MapSummary from '@/components/map/components/summary/ParkinglotSummary'
import RecommendParkinglotView from '@/components/recommendParkinglot/view'
import type { ParkinglotWithDistance } from '@/components/recommendParkinglot/viewModel'

import Footer from '@/shared/components/Footer'
import SectionBar from '@/shared/components/SectionBar'
import type { MotionValue } from 'framer-motion'
import type { LegacyRef } from 'react'

import { SHEET_STATE_TYPE } from '@/enums/detail'

import { ParkinglotDetail, TicketsItem } from '@/api/server/detail/dto/detailDto'

interface ParkinglotProps {
  pin: ParkinglotDetail
  tickets: TicketsItem[]
  isRoadViewOpen: boolean
  setIsRoadViewOpen: React.Dispatch<React.SetStateAction<boolean>>
  sortedParkinglots: ParkinglotWithDistance[]
  sheetType: SHEET_STATE_TYPE
  sheetPositionY: MotionValue<number>
}

const Parkinglot = forwardRef((props: ParkinglotProps, ref: LegacyRef<HTMLDivElement>) => {
  const { pin, tickets, isRoadViewOpen, setIsRoadViewOpen, sortedParkinglots, sheetType, sheetPositionY } = props

  return (
    <AnimationSheet sheetType={sheetType} sheetPositionY={sheetPositionY}>
      <div ref={ref}>
        {sheetType === SHEET_STATE_TYPE.VISIBLE && (
          <>
            <ParkinglotDetailHeaderView
              isPartner={pin.basic.partnerStatus}
              parkinglotName={pin.basic.name}
              parkinglotSeq={pin.seq}
            />
            <div className="relative -mt-[56px] h-[170px] w-full">
              <StaticNaverMap lat={pin.basic.latitude} lng={pin.basic.longitude} />
            </div>
          </>
        )}
        <MapSummary
          sheetType={sheetType}
          pin={pin}
          setIsRoadViewOpen={setIsRoadViewOpen}
          isRoadViewOpen={isRoadViewOpen}
          tickets={tickets}
        />
        {pin.basic.partnerStatus && tickets.length > 0 && (
          <>
            <TicketsListView pin={pin} tickets={tickets} />
            <SectionBar className="my-[24px]" border="border-t-[10px]" />
          </>
        )}
        {sortedParkinglots.length > 0 && <RecommendParkinglotView sortedParkinglots={sortedParkinglots} />}
        <ParkinglotModifyDateView modifyDate={pin.modifyDate} />
        <ParkinglotInfoView pin={pin} />
        <AppBanner
          parkinglotSeq={pin.seq}
          isPartner={pin.basic.partnerStatus}
          latitude={pin.basic.latitude}
          longitude={pin.basic.longitude}
          type={pin.type}
        />
        {pin.aiDescription && pin.aiDescription.response.length > 0 && (
          <ParkinglotDetailMoaiView aiDescription={pin.aiDescription} />
        )}
        <CautionView />
        <Footer />
      </div>
    </AnimationSheet>
  )
})

Parkinglot.displayName = 'Parkinglot'

export default Parkinglot
